import { fetchActivitiesThunk } from '@spike/activities-action';
import { Activity, ActivityPagination } from '@spike/activity-model';
import { Period } from '@spike/model';
import { CareReport } from '@spike/report-model';
import { showErrorThunk, showSuccessThunk } from '@spike/notifications-action';
import { getCareReportByAppointmentIdThunk, saveCareReportThunk, sendCareReportThunk } from '@spike/reports-action';
import { useApiClientWrapper, useTimeZone } from 'hooks';
import moment, { Moment } from 'moment-timezone';
import React from 'react';
import { dispatch } from 'store';
import { ReportServiceContext } from './types';

const RECENT_ACTIVITIES_PAGE_SIZE = 10;

interface ActivityGroup {
    dateKey: string;
    activities: Activity[];
}

// Consolidated report-document state. A single object keeps the many related
// fields in sync and lets `applyLoadedReport` seed everything in one update.
interface ReportState {
    id?: number;
    createdAt?: string;
    updatedAt?: string;
    sentAt?: string | null;
    pdfUrl: string | null;
    generalNotes: string;
    activities: Activity[];
    petAvatarUrl: string;
    petAvatarFile: File | null;
    removePetImage: boolean;
    hasCustomImage: boolean;
    // activityId → reportCareActivityId for persisted selections (used for _destroy)
    persistedSelectionMap: Map<number, number>;
    // reportCareActivityIds to delete on next save
    removedSelectionIds: Set<number>;
    isSaving: boolean;
    saveError: string | null;
    // bumped on every load so mount-initialized inputs (e.g. Textarea) re-seed
    revision: number;
    // true when the report has edits not yet persisted; reset on load and successful save
    isDirty: boolean;
}

// Recent-activities panel state (independent pagination concern).
interface RecentState {
    items: Activity[];
    pagination?: ActivityPagination;
    page: number;
    isLoading: boolean;
}

interface ReportBuilderContextValue {
    bookingId: number;
    generalNotes: string;
    petAvatar: string;
    hasCustomPetAvatar: boolean;
    activities: Activity[];
    groupedActivities: ActivityGroup[];
    recentActivities: Activity[];
    isRecentActivitiesLoading: boolean;
    hasMoreRecentActivities: boolean;
    isSaving: boolean;
    saveError: string | null;
    hasPersistedReport: boolean;
    hasUnsavedChanges: boolean;
    reportDate: Moment;
    pdfUrl: string | null;
    reportRevision: number;
    /* eslint-disable no-unused-vars */
    setGeneralNotes: (_notes: string) => void;
    setPetAvatar: (_url: string, _file: File) => void;
    removePetAvatar: () => void;
    addActivity: (_activity: Activity) => void;
    updateActivity: (_activity: Activity) => void;
    removeActivity: (_activity: Activity) => void;
    addExistingActivity: (_activity: Activity) => void;
    removeExistingActivityFromReport: (_activity: Activity) => void;
    /* eslint-enable no-unused-vars */
    loadMoreRecentActivities: () => void;
    saveReport: () => Promise<void>;
    sendReport: (_email?: string) => Promise<boolean>;
}

interface ApiErrorResponse {
    response?: { data?: { errors?: string[] } };
}

// Pull the first server-provided error message, falling back to a caller-supplied default.
const extractErrorMessage = (error: unknown, fallback: string): string =>
    (error as ApiErrorResponse)?.response?.data?.errors?.[0] ?? fallback;

const isSameActivity = (a: Activity, b: Activity): boolean => a.uuid === b.uuid || (a.id > 0 && a.id === b.id);

const sortByTimeAsc = (items: Activity[]): Activity[] => [...items].sort((a, b) => a.date.valueOf() - b.date.valueOf());

const groupActivitiesByDay = (activities: Activity[]): ActivityGroup[] => {
    const sorted = sortByTimeAsc(activities);
    const map = new Map<string, Activity[]>();

    for (const activity of sorted) {
        const key = activity.date.format('YYYY-MM-DD');
        const group = map.get(key);
        if (group) {
            group.push(activity);
        } else {
            map.set(key, [activity]);
        }
    }

    return Array.from(map.entries()).map(([dateKey, acts]) => ({ dateKey, activities: acts }));
};

// Editing an existing report shows its last-edited (then created) date; a brand-new
// report shows now. After a save the PATCH response refreshes updatedAt, so the date
// reflects the edit immediately.
const computeReportDate = (
    createdAt: string | null | undefined,
    updatedAt: string | null | undefined,
    timeZone: string
): Moment => {
    if (updatedAt) return moment(updatedAt).tz(timeZone);
    if (createdAt) return moment(createdAt).tz(timeZone);
    return moment().tz(timeZone);
};

const upsertActivity = (activities: Activity[], activity: Activity): Activity[] =>
    activities.find(a => isSameActivity(a, activity))
        ? activities.map(a => (isSameActivity(a, activity) ? activity : a))
        : [...activities, activity];

// Add the activity's persisted selection id to the removal set (no-op for unsaved activities).
const trackRemoval = (state: ReportState, activity: Activity): Set<number> => {
    if (activity.id > 0) {
        const selectionId = state.persistedSelectionMap.get(activity.id);
        if (selectionId !== undefined && !state.removedSelectionIds.has(selectionId)) {
            return new Set(state.removedSelectionIds).add(selectionId);
        }
    }
    return state.removedSelectionIds;
};

// Undo a pending removal when a previously-removed activity is re-added.
const untrackRemoval = (state: ReportState, activity: Activity): Set<number> => {
    if (activity.id > 0) {
        const selectionId = state.persistedSelectionMap.get(activity.id);
        if (selectionId !== undefined && state.removedSelectionIds.has(selectionId)) {
            const next = new Set(state.removedSelectionIds);
            next.delete(selectionId);
            return next;
        }
    }
    return state.removedSelectionIds;
};

const ReportBuilderContext = React.createContext<ReportBuilderContextValue | null>(null);

interface ReportBuilderProviderProps {
    appointmentId: number;
    bookingId: number;
    petId: number;
    datePeriod: Period;
    serviceContext: ReportServiceContext;
    initialPetAvatar?: string;
    petParentEmail?: string;
    children: React.ReactNode;
}

export const ReportBuilderProvider: React.FC<ReportBuilderProviderProps> = ({
    appointmentId,
    bookingId,
    petId,
    datePeriod,
    serviceContext,
    initialPetAvatar,
    petParentEmail,
    children
}) => {
    const apiClientWrapper = useApiClientWrapper();
    const timeZone = useTimeZone();

    const [report, setReport] = React.useState<ReportState>(() => ({
        pdfUrl: null,
        generalNotes: '',
        activities: [],
        petAvatarUrl: initialPetAvatar ?? '',
        petAvatarFile: null,
        removePetImage: false,
        hasCustomImage: false,
        persistedSelectionMap: new Map<number, number>(),
        removedSelectionIds: new Set<number>(),
        isSaving: false,
        saveError: null,
        revision: 0,
        isDirty: false
    }));

    const [recent, setRecent] = React.useState<RecentState>({
        items: [],
        pagination: undefined,
        page: 1,
        isLoading: false
    });

    const groupedActivities = React.useMemo(() => groupActivitiesByDay(report.activities), [report.activities]);

    const hasMoreRecentActivities = React.useMemo(
        () => recent.page < (recent.pagination?.totalPages ?? 1),
        [recent.page, recent.pagination]
    );

    // Report date: server timestamp when saved, else now
    const reportDate = React.useMemo(
        () => computeReportDate(report.createdAt, report.updatedAt, timeZone),
        [report.createdAt, report.updatedAt, timeZone]
    );

    const hasCustomPetAvatar = React.useMemo(
        () => report.petAvatarFile !== null || (report.hasCustomImage && !report.removePetImage),
        [report.petAvatarFile, report.hasCustomImage, report.removePetImage]
    );

    const applyLoadedReport = React.useCallback(
        (loaded: CareReport) => {
            setReport(prev => ({
                ...prev,
                id: loaded.id,
                createdAt: loaded.createdAt,
                updatedAt: loaded.updatedAt,
                sentAt: loaded.sentAt,
                pdfUrl: loaded.pdfUrl,
                generalNotes: loaded.generalNotes,
                activities: loaded.activities,
                // Prefer the report-builder image; fall back to the pet's default image.
                petAvatarUrl: loaded.petImageUrl ?? loaded.pet.image ?? initialPetAvatar ?? '',
                petAvatarFile: null,
                removePetImage: false,
                hasCustomImage: loaded.petImageUrl != null,
                persistedSelectionMap: new Map<number, number>(
                    Object.entries(loaded.activitySelectionIds).map(([actId, selId]) => [Number(actId), selId])
                ),
                removedSelectionIds: new Set<number>(),
                isSaving: false,
                saveError: null,
                revision: prev.revision + 1,
                isDirty: false
            }));
        },
        [initialPetAvatar]
    );

    // Load existing report on mount
    React.useEffect(() => {
        dispatch(getCareReportByAppointmentIdThunk(apiClientWrapper, appointmentId))
            .then(loaded => {
                if (loaded) {
                    applyLoadedReport(loaded);
                }
            })
            .catch((err: unknown) => {
                dispatch(showErrorThunk(extractErrorMessage(err, 'Failed to load report.')));
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookingId]);

    const mergeIntoRecentActivities = React.useCallback((activity: Activity) => {
        if (activity.id <= 0) return;
        setRecent(prev => {
            const exists = prev.items.find(a => isSameActivity(a, activity));
            return {
                ...prev,
                items: exists
                    ? prev.items.map(a => (isSameActivity(a, activity) ? activity : a))
                    : [activity, ...prev.items]
            };
        });
    }, []);

    const addActivity = React.useCallback(
        (activity: Activity) => {
            setReport(prev => ({
                ...prev,
                activities: upsertActivity(prev.activities, activity),
                removedSelectionIds: untrackRemoval(prev, activity),
                isDirty: true
            }));
            mergeIntoRecentActivities(activity);
        },
        [mergeIntoRecentActivities]
    );

    const updateActivity = React.useCallback(
        (activity: Activity) => {
            setReport(prev => ({
                ...prev,
                activities: prev.activities.map(a => (isSameActivity(a, activity) ? activity : a)),
                isDirty: true
            }));
            mergeIntoRecentActivities(activity);
        },
        [mergeIntoRecentActivities]
    );

    const removeActivity = React.useCallback((activity: Activity) => {
        setReport(prev => ({
            ...prev,
            activities: prev.activities.filter(a => !isSameActivity(a, activity)),
            removedSelectionIds: trackRemoval(prev, activity),
            isDirty: true
        }));
    }, []);

    const addExistingActivity = React.useCallback((activity: Activity) => {
        setReport(prev => {
            const exists = prev.activities.find(a => isSameActivity(a, activity));
            return {
                ...prev,
                activities: exists ? prev.activities : [...prev.activities, activity],
                removedSelectionIds: untrackRemoval(prev, activity),
                isDirty: true
            };
        });
    }, []);

    const removeExistingActivityFromReport = React.useCallback((activity: Activity) => {
        setReport(prev => ({
            ...prev,
            activities: prev.activities.filter(a => !isSameActivity(a, activity)),
            removedSelectionIds: trackRemoval(prev, activity),
            isDirty: true
        }));
    }, []);

    const fetchRecentActivities = React.useCallback(
        async (page: number) => {
            setRecent(prev => ({ ...prev, isLoading: true }));
            try {
                const result = await dispatch(
                    fetchActivitiesThunk(
                        apiClientWrapper,
                        petId,
                        page,
                        RECENT_ACTIVITIES_PAGE_SIZE,
                        bookingId,
                        undefined,
                        datePeriod
                    )
                );
                setRecent(prev => ({
                    ...prev,
                    items: page === 1 ? result.activities : [...prev.items, ...result.activities],
                    pagination: result.pagination,
                    isLoading: false
                }));
            } catch (err: unknown) {
                dispatch(showErrorThunk(extractErrorMessage(err, 'Failed to load recent activities.')));
                setRecent(prev => ({ ...prev, isLoading: false }));
            }
        },
        [apiClientWrapper, petId, bookingId, datePeriod]
    );

    const loadMoreRecentActivities = React.useCallback(() => {
        if (recent.isLoading || !hasMoreRecentActivities) return;
        setRecent(prev => ({ ...prev, page: prev.page + 1 }));
    }, [recent.isLoading, hasMoreRecentActivities]);

    React.useEffect(() => {
        void fetchRecentActivities(recent.page);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recent.page, petId, bookingId, datePeriod.from.valueOf(), datePeriod.to.valueOf()]);

    const setGeneralNotes = React.useCallback((notes: string) => {
        setReport(prev => ({ ...prev, generalNotes: notes, isDirty: true }));
    }, []);

    const setPetAvatar = React.useCallback((url: string, file: File) => {
        setReport(prev => ({ ...prev, petAvatarUrl: url, petAvatarFile: file, removePetImage: false, isDirty: true }));
    }, []);

    const removePetAvatar = React.useCallback(() => {
        setReport(prev => ({
            ...prev,
            petAvatarUrl: initialPetAvatar ?? '',
            petAvatarFile: null,
            removePetImage: true,
            hasCustomImage: false,
            isDirty: true
        }));
    }, [initialPetAvatar]);

    const saveReport = React.useCallback(async () => {
        setReport(prev => ({ ...prev, isSaving: true, saveError: null }));

        // Only send genuinely new activities; existing ones are keyed by their
        // persisted selection id and must never be re-sent as bare { activity_id }.
        const activitiesToAdd = report.activities
            .filter(a => a.id > 0 && !report.persistedSelectionMap.has(a.id))
            .map(a => a.id);

        try {
            const saved = await dispatch(
                saveCareReportThunk(apiClientWrapper, {
                    id: report.id,
                    appointmentId,
                    serviceType: serviceContext,
                    generalNotes: report.generalNotes,
                    activitiesToAdd,
                    selectionIdsToRemove: Array.from(report.removedSelectionIds),
                    petImageFile: report.petAvatarFile ?? undefined,
                    removePetImage: report.removePetImage || undefined
                })
            );

            applyLoadedReport(saved);
            dispatch(showSuccessThunk('Report saved successfully'));
        } catch (err: unknown) {
            const message = extractErrorMessage(err, 'Failed to save report.');
            setReport(prev => ({ ...prev, isSaving: false, saveError: message }));
        }
    }, [apiClientWrapper, appointmentId, serviceContext, report, applyLoadedReport]);

    const sendReport = React.useCallback(
        async (email?: string): Promise<boolean> => {
            if (!report.id) return false;

            setReport(prev => ({ ...prev, saveError: null }));
            try {
                const sent = await dispatch(sendCareReportThunk(apiClientWrapper, report.id, email ?? petParentEmail));
                applyLoadedReport(sent);
                dispatch(showSuccessThunk('Report sent successfully'));
                return true;
            } catch (err: unknown) {
                const message = extractErrorMessage(err, 'Failed to send report.');
                setReport(prev => ({ ...prev, saveError: message }));
                return false;
            }
        },
        [apiClientWrapper, report.id, petParentEmail, applyLoadedReport]
    );

    const contextValue = React.useMemo<ReportBuilderContextValue>(
        () => ({
            bookingId,
            generalNotes: report.generalNotes,
            petAvatar: report.petAvatarUrl,
            hasCustomPetAvatar,
            activities: report.activities,
            groupedActivities,
            recentActivities: recent.items,
            isRecentActivitiesLoading: recent.isLoading,
            hasMoreRecentActivities,
            isSaving: report.isSaving,
            saveError: report.saveError,
            hasPersistedReport: report.id != null,
            hasUnsavedChanges: report.isDirty,
            reportDate,
            pdfUrl: report.pdfUrl,
            reportRevision: report.revision,
            setGeneralNotes,
            setPetAvatar,
            removePetAvatar,
            addActivity,
            updateActivity,
            removeActivity,
            addExistingActivity,
            removeExistingActivityFromReport,
            loadMoreRecentActivities,
            saveReport,
            sendReport
        }),
        [
            bookingId,
            report.generalNotes,
            report.petAvatarUrl,
            hasCustomPetAvatar,
            report.activities,
            groupedActivities,
            recent.items,
            recent.isLoading,
            hasMoreRecentActivities,
            report.isSaving,
            report.saveError,
            report.id,
            report.isDirty,
            reportDate,
            report.pdfUrl,
            report.revision,
            setGeneralNotes,
            setPetAvatar,
            removePetAvatar,
            addActivity,
            updateActivity,
            removeActivity,
            addExistingActivity,
            removeExistingActivityFromReport,
            loadMoreRecentActivities,
            saveReport,
            sendReport
        ]
    );

    return <ReportBuilderContext.Provider value={contextValue}>{children}</ReportBuilderContext.Provider>;
};

export const useReportBuilder = (): ReportBuilderContextValue => {
    const context = React.useContext(ReportBuilderContext);
    if (!context) {
        throw new Error('useReportBuilder must be used within a ReportBuilderProvider');
    }
    return context;
};
